<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div class="main-container request-admin-page">
        <div v-if="loading" class="request-admin-loader">
          <div class="spinner-border text-secondary" role="status"></div>
          <span>Cargando solicitud...</span>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <template v-else-if="solicitud">
          <header class="request-admin-header">
            <div class="header-main">
              <button type="button" class="back-button" @click="$router.back()">
                <b-icon icon="arrow-left" />
              </button>
              <div>
                <div class="eyebrow">Detalle operativo</div>
                <h1>Solicitud #{{ solicitud.id }}</h1>
                <div class="header-meta">
                  <span :class="['status-badge', requestStatusClass]">{{ requestStatusLabel(solicitud.estado) }}</span>
                  <span>{{ formatDateTime(solicitud.fecha_creado_iso) }}</span>
                  <span v-if="solicitud.clase">Clase {{ solicitud.clase }}</span>
                  <span v-if="empresa">{{ empresa.nombre }}</span>
                </div>
              </div>
            </div>
            <div class="header-actions">
              <router-link
                v-if="firstOrder"
                class="action-button secondary"
                :to="`/OrdenDetalleAdmin/${firstOrder.id}`"
              >
                <b-icon icon="cart2" />
                Ver orden principal
              </router-link>
              <router-link
                v-if="cliente"
                class="action-button primary"
                :to="`/superadmin/Usuarios/${cliente.id}`"
              >
                <b-icon icon="person" />
                Cliente
              </router-link>
            </div>
          </header>

          <section class="summary-grid">
            <div class="summary-card">
              <div class="summary-icon customer"><b-icon icon="person" /></div>
              <div class="summary-copy">
                <span class="summary-label">Cliente</span>
                <strong>{{ customerName }}</strong>
                <span>{{ customerContact }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon orders"><b-icon icon="cart2" /></div>
              <div class="summary-copy">
                <span class="summary-label">Ordenes</span>
                <strong>{{ ordenes.length }}</strong>
                <span>{{ activeOrdersCount }} activas</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon conversation"><b-icon icon="chat-dots" /></div>
              <div class="summary-copy">
                <span class="summary-label">Conversacion</span>
                <strong>{{ totalMessagesCount }} mensajes</strong>
                <span>{{ conversaciones.length }} profesional{{ conversaciones.length === 1 ? '' : 'es' }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon money"><b-icon icon="receipt" /></div>
              <div class="summary-copy">
                <span class="summary-label">Presupuestos</span>
                <strong>{{ allBudgets.length }}</strong>
                <span>{{ approvedBudgetsCount }} aprobados</span>
              </div>
            </div>
          </section>

          <div class="content-grid">
            <main class="content-main">
              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon timeline-icon"><b-icon icon="clock-history" /></span>Linea de tiempo</h2>
                </div>
                <div v-if="timeline.length" class="timeline">
                  <div v-for="event in timeline" :key="event.key" class="timeline-item">
                    <div class="timeline-dot" :class="event.kind"></div>
                    <div>
                      <div class="timeline-title">{{ event.title }}</div>
                      <div class="timeline-date">{{ formatDateTime(event.date) }}</div>
                      <p v-if="event.description">{{ event.description }}</p>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin eventos registrados.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon conversation-icon"><b-icon icon="chat-dots" /></span>Conversacion con profesionales</h2>
                  <span>{{ totalMessagesCount }} mensaje{{ totalMessagesCount === 1 ? '' : 's' }}</span>
                </div>

                <div v-if="conversationTabs.length > 1" class="conversation-tabs" role="tablist" aria-label="Conversaciones por profesional">
                  <button
                    v-for="conversation in conversationTabs"
                    :key="conversation.key"
                    type="button"
                    class="conversation-tab"
                    :class="{ active: activeConversationKeyResolved === conversation.key }"
                    @click="setActiveConversation(conversation.key)"
                  >
                    <span>{{ conversationLabel(conversation) }}</span>
                    <small>{{ conversation.message_count || (conversation.mensajes || []).length }}</small>
                  </button>
                </div>

                <div v-if="activeMessages.length" class="request-conversation">
                  <article
                    v-for="mensaje in activeMessages"
                    :key="mensaje.id"
                    class="conversation-message"
                    :class="messageBubbleClass(mensaje)"
                  >
                    <div class="message-heading">
                      <span>{{ messageSenderLabel(mensaje) }}</span>
                      <time>{{ formatDateTime(mensaje.fecha_iso) }}</time>
                    </div>
                    <p>{{ messageText(mensaje) }}</p>
                    <div v-if="messagePhotos(mensaje).length" class="message-photos">
                      <a
                        v-for="foto in messagePhotos(mensaje)"
                        :key="`msg-${mensaje.id}-foto-${foto.id}`"
                        :href="foto.link || foto.url"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-icon icon="image" />
                        Foto #{{ foto.id }}
                      </a>
                    </div>
                    <div v-if="mensaje.mensaje_original && mensaje.mensaje_original !== mensaje.mensaje" class="message-original">
                      Original: {{ mensaje.mensaje_original }}
                    </div>
                  </article>
                </div>
                <div v-else class="empty-state">Sin mensajes en esta conversacion.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon orders-icon"><b-icon icon="cart2" /></span>Ordenes asociadas</h2>
                  <span>{{ ordenes.length }}</span>
                </div>
                <div v-if="ordenes.length" class="order-list">
                  <article v-for="orden in ordenes" :key="orden.id" class="order-row">
                    <div class="order-main">
                      <div class="order-title">
                        <router-link :to="`/OrdenDetalleAdmin/${orden.id}`">Orden #{{ orden.id }}</router-link>
                        <span :class="['mini-badge', orderStatusClass(orden.estado)]">{{ orden.estado_label || orderStatusLabel(orden.estado) }}</span>
                      </div>
                      <div class="order-meta">
                        <span>{{ professionalName(orden.profesional) }}</span>
                        <span>{{ formatDate(orden.fecha_iso) }}</span>
                        <span>{{ scheduleLabel(orden) }}</span>
                      </div>
                      <div v-if="orden.servicios && orden.servicios.length" class="chips-list">
                        <span v-for="servicio in orden.servicios.slice(0, 5)" :key="servicio.id" class="service-chip">
                          {{ servicio.nombre || 'Item' }}
                        </span>
                        <span v-if="orden.servicios.length > 5" class="service-chip muted">+{{ orden.servicios.length - 5 }}</span>
                      </div>
                    </div>
                    <div class="order-side">
                      <strong>{{ formatCurrency(orden.monto_total || orden.monto) }}</strong>
                      <span>Pagado {{ formatCurrency(orden.monto_pagado) }}</span>
                      <router-link :to="`/OrdenDetalleAdmin/${orden.id}`" class="inline-link">Inspeccionar</router-link>
                    </div>
                  </article>
                </div>
                <div v-else class="empty-state">Sin ordenes asociadas.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon budget-icon"><b-icon icon="receipt" /></span>Presupuestos</h2>
                  <span>{{ allBudgets.length }}</span>
                </div>
                <div v-if="allBudgets.length" class="table-wrap">
                  <table class="admin-table">
                    <thead>
                      <tr>
                        <th>Presupuesto</th>
                        <th>Orden</th>
                        <th>Profesional</th>
                        <th class="text-right">Monto</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="presupuesto in allBudgets" :key="budgetKey(presupuesto)">
                        <td>
                          <strong>{{ presupuesto.nombre || presupuesto.descripcion || `Presupuesto #${presupuesto.id}` }}</strong>
                          <small>Creado {{ formatDateTime(presupuesto.fecha_creado_iso) }}</small>
                          <small v-if="presupuesto.fecha_enviado_iso">Enviado {{ formatDateTime(presupuesto.fecha_enviado_iso) }}</small>
                        </td>
                        <td>
                          <router-link v-if="presupuesto.orden_id" :to="`/OrdenDetalleAdmin/${presupuesto.orden_id}`">
                            #{{ presupuesto.orden_id }}
                          </router-link>
                          <span v-else>Directo</span>
                        </td>
                        <td>{{ professionalName(professionalForBudget(presupuesto)) }}</td>
                        <td class="text-right">{{ formatCurrency(presupuesto.monto_total || budgetTotal(presupuesto)) }}</td>
                        <td>
                          <span :class="['mini-badge', budgetStatusClass(presupuesto.estado)]">{{ budgetStatusLabel(presupuesto.estado) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-state">Sin presupuestos registrados.</div>
              </section>
            </main>

            <aside class="content-side">
              <section class="panel contact-panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon customer-icon"><b-icon icon="person-lines-fill" /></span>Cliente</h2>
                </div>
                <div class="contact-name">{{ customerName }}</div>
                <a v-if="solicitud.email" :href="`mailto:${solicitud.email}`">{{ solicitud.email }}</a>
                <a v-if="solicitud.telefono" :href="`tel:${solicitud.telefono}`">{{ solicitud.telefono }}</a>
                <div class="detail-line">
                  <span>Direccion</span>
                  <strong>{{ fullAddress }}</strong>
                </div>
                <router-link v-if="cliente" class="inline-link" :to="`/superadmin/Usuarios/${cliente.id}`">
                  Ver detalle de usuario
                </router-link>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon request-icon"><b-icon icon="inbox" /></span>Solicitud</h2>
                </div>
                <div class="detail-line">
                  <span>Titulo</span>
                  <strong>{{ solicitud.titulo || 'Sin titulo' }}</strong>
                </div>
                <div class="detail-line">
                  <span>Categoria</span>
                  <strong>{{ categoryName }}</strong>
                </div>
                <div class="detail-line">
                  <span>Origen</span>
                  <strong>{{ sourceLabel }}</strong>
                </div>
                <p v-if="solicitud.descripcion" class="description-text">{{ solicitud.descripcion }}</p>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon professional-icon"><b-icon icon="person-badge" /></span>Profesionales</h2>
                  <span>{{ professionalsInvolved.length }}</span>
                </div>
                <div v-if="professionalsInvolved.length" class="stack-list compact">
                  <div v-for="profesional in professionalsInvolved" :key="profesional.id || profesional.key" class="list-row">
                    <div>
                      <strong>{{ professionalName(profesional) }}</strong>
                      <span>{{ profesional.email || profesional.telefono || 'Sin contacto' }}</span>
                    </div>
                    <router-link v-if="profesional.id" :to="`/VerProfesional/${profesional.id}`" class="inline-link">
                      Ver
                    </router-link>
                  </div>
                </div>
                <div v-else class="empty-state">Sin profesionales asociados.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon files-icon"><b-icon icon="file-earmark-image" /></span>Fotos solicitud</h2>
                  <span>{{ rootPhotos.length }}</span>
                </div>
                <div v-if="rootPhotos.length" class="photo-grid">
                  <a
                    v-for="foto in rootPhotos"
                    :key="foto.id"
                    :href="foto.link"
                    target="_blank"
                    rel="noopener"
                    class="photo-thumb"
                    :style="{ backgroundImage: `url(${foto.link})` }"
                    :aria-label="`Foto ${foto.id}`"
                  ></a>
                </div>
                <div v-else class="empty-state">Sin fotos iniciales.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon bot-icon"><b-icon icon="robot" /></span>Autorespuestas</h2>
                  <span>{{ entradasAutorespuesta.length }}</span>
                </div>
                <div v-if="entradasAutorespuesta.length" class="stack-list compact">
                  <div v-for="entrada in entradasAutorespuesta" :key="entrada.id" class="list-row">
                    <div>
                      <strong>{{ formatDateTime(entrada.fecha) }}</strong>
                      <span>{{ entrada.message || 'Sin mensaje' }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin entradas de bot.</div>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'

export default {
  name: 'SolicitudDetalleAdmin',
  components: {
    SideNav,
    TopNav
  },
  data () {
    return {
      loading: true,
      error: '',
      payload: null,
      activeConversationKey: null
    }
  },
  computed: {
    solicitud () {
      return this.payload && this.payload.solicitud ? this.payload.solicitud : null
    },
    cliente () {
      return this.payload ? this.payload.cliente : null
    },
    empresa () {
      return this.payload ? this.payload.empresa : null
    },
    ordenes () {
      return this.payload && Array.isArray(this.payload.ordenes) ? this.payload.ordenes : []
    },
    presupuestos () {
      return this.payload && Array.isArray(this.payload.presupuestos) ? this.payload.presupuestos : []
    },
    conversaciones () {
      return this.payload && Array.isArray(this.payload.conversaciones) ? this.payload.conversaciones : []
    },
    entradasAutorespuesta () {
      return this.payload && Array.isArray(this.payload.entradas_autorespuesta) ? this.payload.entradas_autorespuesta : []
    },
    fotos () {
      return this.payload && Array.isArray(this.payload.fotos) ? this.payload.fotos : []
    },
    rootPhotos () {
      return this.fotos.filter(foto => foto && foto.link && !foto.mensaje_cliente_id && !foto.mensaje_profesional_id)
    },
    firstOrder () {
      return this.ordenes.length ? this.ordenes[0] : null
    },
    activeOrdersCount () {
      return this.ordenes.filter(orden => ![4, 10].includes(Number(orden.estado))).length
    },
    allBudgets () {
      const budgets = []
      const seen = new Set()
      this.presupuestos.forEach(presupuesto => {
        if (!presupuesto || seen.has(presupuesto.id)) return
        seen.add(presupuesto.id)
        budgets.push(presupuesto)
      })
      this.ordenes.forEach(orden => {
        ;(orden.presupuestos || []).forEach(presupuesto => {
          if (!presupuesto || seen.has(presupuesto.id)) return
          seen.add(presupuesto.id)
          budgets.push({ ...presupuesto, orden_id: presupuesto.orden_id || orden.id })
        })
      })
      return budgets.sort((a, b) => new Date(b.fecha_creado_iso || 0) - new Date(a.fecha_creado_iso || 0))
    },
    approvedBudgetsCount () {
      return this.allBudgets.filter(presupuesto => Number(presupuesto.estado) === 2).length
    },
    totalMessagesCount () {
      const ids = new Set()
      this.conversaciones.forEach(conversation => {
        ;(conversation.mensajes || []).forEach(mensaje => {
          ids.add(mensaje && mensaje.id ? mensaje.id : `${conversation.key}-${ids.size}`)
        })
      })
      return ids.size
    },
    conversationTabs () {
      return this.conversaciones.map((conversation, index) => ({
        ...conversation,
        key: conversation.key || `conversation-${conversation.profesional_id || index}`,
        mensajes: Array.isArray(conversation.mensajes) ? conversation.mensajes : []
      }))
    },
    activeConversationKeyResolved () {
      if (!this.conversationTabs.length) return null
      const activeKey = this.activeConversationKey
      if (activeKey && this.conversationTabs.some(conversation => conversation.key === activeKey)) return activeKey
      const assignedKey = this.solicitud && this.solicitud.profesional_id ? `prof-${this.solicitud.profesional_id}` : null
      if (assignedKey && this.conversationTabs.some(conversation => conversation.key === assignedKey)) return assignedKey
      return this.conversationTabs[0].key
    },
    activeConversation () {
      const key = this.activeConversationKeyResolved
      return this.conversationTabs.find(conversation => conversation.key === key) || null
    },
    activeMessages () {
      const mensajes = this.activeConversation && Array.isArray(this.activeConversation.mensajes)
        ? this.activeConversation.mensajes
        : []
      return mensajes.slice().sort((a, b) => new Date(a.fecha_iso || 0) - new Date(b.fecha_iso || 0))
    },
    customerName () {
      const solicitudName = `${this.solicitud.nombre || ''} ${this.solicitud.apellido || ''}`.trim()
      if (solicitudName) return solicitudName
      if (this.cliente) {
        const name = `${this.cliente.nombre || ''} ${this.cliente.apellidos || ''}`.trim()
        if (name) return name
      }
      return 'Cliente sin nombre'
    },
    customerContact () {
      return this.solicitud.email || this.solicitud.telefono || (this.cliente && (this.cliente.email || this.cliente.telefono)) || 'Sin contacto'
    },
    fullAddress () {
      const parts = [
        this.solicitud.direccion,
        this.solicitud.numero_apartamento ? `Depto/Casa ${this.solicitud.numero_apartamento}` : '',
        this.solicitud.comuna && this.solicitud.comuna.nombre ? this.solicitud.comuna.nombre : ''
      ].filter(Boolean)
      return parts.join(', ') || 'Sin direccion'
    },
    categoryName () {
      return this.solicitud.categoria && this.solicitud.categoria.nombre ? this.solicitud.categoria.nombre : 'Sin categoria'
    },
    sourceLabel () {
      if (this.empresa) return this.empresa.nombre
      if (this.payload && this.payload.lead_partner) return this.payload.lead_partner.nombre || 'Lead partner'
      return this.solicitud.utm_source || 'Jobbing'
    },
    requestStatusClass () {
      const classes = {
        0: 'muted',
        1: 'success',
        2: 'primary',
        3: 'warning',
        10: 'danger'
      }
      return classes[this.solicitud.estado] || 'muted'
    },
    professionalsInvolved () {
      const map = new Map()
      const add = profesional => {
        if (!profesional) return
        const key = profesional.id || profesional.key || `${profesional.nombre || ''}-${profesional.email || ''}`
        if (!key || map.has(key)) return
        map.set(key, profesional)
      }
      add(this.payload && this.payload.profesional_asignado)
      add(this.payload && this.payload.profesional_original)
      ;(this.payload && this.payload.profesionales_visibles ? this.payload.profesionales_visibles : []).forEach(add)
      this.ordenes.forEach(orden => add(orden.profesional))
      this.conversaciones.forEach(conversation => add(conversation.profesional))
      return Array.from(map.values())
    },
    timeline () {
      const events = []
      if (!this.solicitud) return events
      this.addTimeline(events, 'created', 'Solicitud creada', this.solicitud.fecha_creado_iso, this.customerName, 'info')
      this.conversaciones.forEach(conversation => {
        const lastMessage = (conversation.mensajes || []).slice().sort((a, b) => new Date(b.fecha_iso || 0) - new Date(a.fecha_iso || 0))[0]
        if (lastMessage) {
          this.addTimeline(events, `message-${conversation.key}`, `Ultimo mensaje: ${this.conversationLabel(conversation)}`, lastMessage.fecha_iso, this.messageText(lastMessage), 'primary')
        }
      })
      this.ordenes.forEach(orden => {
        this.addTimeline(events, `orden-${orden.id}`, `Orden ${orden.estado_label || this.orderStatusLabel(orden.estado)}`, orden.fecha_creado_iso, `Orden #${orden.id}`, 'success')
        this.addTimeline(events, `orden-agenda-${orden.id}`, 'Atencion agendada', orden.fecha_iso, this.scheduleLabel(orden), 'primary')
        this.addTimeline(events, `orden-completed-${orden.id}`, 'Orden completada', orden.fecha_completado_iso, `Orden #${orden.id}`, 'success')
      })
      this.allBudgets.forEach(presupuesto => {
        this.addTimeline(events, `budget-${presupuesto.id}`, `Presupuesto ${this.budgetStatusLabel(presupuesto.estado)}`, presupuesto.fecha_creado_iso, this.formatCurrency(presupuesto.monto_total || this.budgetTotal(presupuesto)), 'warning')
        this.addTimeline(events, `budget-sent-${presupuesto.id}`, 'Presupuesto enviado', presupuesto.fecha_enviado_iso, presupuesto.nombre || `#${presupuesto.id}`, 'warning')
        this.addTimeline(events, `budget-approved-${presupuesto.id}`, 'Presupuesto aprobado', presupuesto.fecha_aprobado_iso, presupuesto.nombre || `#${presupuesto.id}`, 'success')
      })
      return events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler () {
        this.fetchSolicitud()
      }
    }
  },
  methods: {
    fetchSolicitud () {
      const id = this.$route.params.id
      this.loading = true
      this.error = ''
      axios.get(`${window.hostname}api/admin/solicitudes/${id}/detalle`)
        .then(response => {
          this.payload = response.data
          this.activeConversationKey = null
        })
        .catch(error => {
          console.error('Error loading request detail', error)
          this.error = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'No se pudo cargar la solicitud.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    addTimeline (events, key, title, date, description, kind) {
      if (!date) return
      events.push({ key, title, date, description, kind })
    },
    setActiveConversation (key) {
      this.activeConversationKey = key
    },
    conversationLabel (conversation) {
      if (!conversation) return 'Conversacion'
      const professional = conversation.profesional || {}
      const name = `${professional.nombre || ''} ${professional.apellido || ''}`.trim()
      if (name) return name
      if (conversation.profesional_id) return `Profesional #${conversation.profesional_id}`
      return 'General'
    },
    professionalName (profesional) {
      if (!profesional) return 'Sin profesional'
      return `${profesional.nombre || ''} ${profesional.apellido || ''}`.trim() || `Profesional #${profesional.id}`
    },
    professionalForBudget (presupuesto) {
      if (!presupuesto || !presupuesto.profesional_id) return null
      return this.professionalsInvolved.find(profesional => profesional.id === presupuesto.profesional_id) || null
    },
    formatCurrency (value) {
      const number = Number(value) || 0
      return number.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
    },
    formatDate (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatDateTime (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleString('es-CL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatMinutes (value) {
      if (value === null || value === undefined || value === '') return ''
      const total = Number(value)
      if (!Number.isFinite(total)) return ''
      const hours = Math.floor(total / 60)
      const minutes = total % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    scheduleLabel (orden) {
      const from = this.formatMinutes(orden && orden.desde)
      const to = this.formatMinutes(orden && orden.hasta)
      if (from && to) return `${from} - ${to}`
      return 'Sin horario'
    },
    requestStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Activa',
        2: 'Terminada',
        3: 'En revision',
        10: 'Eliminada'
      }
      return labels[estado] || `Estado ${estado}`
    },
    orderStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Agendada',
        2: 'Confirmada',
        3: 'En proceso',
        4: 'Atendida',
        10: 'Anulada'
      }
      return labels[estado] || `Estado ${estado}`
    },
    orderStatusClass (estado) {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'primary',
        3: 'warning',
        4: 'success',
        10: 'danger'
      }
      return classes[estado] || 'muted'
    },
    budgetStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Enviado',
        2: 'Aceptado',
        3: 'Completado',
        10: 'Rechazado'
      }
      return labels[estado] || `Estado ${estado}`
    },
    budgetStatusClass (estado) {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'success',
        3: 'primary',
        10: 'danger'
      }
      return classes[estado] || 'muted'
    },
    budgetTotal (presupuesto) {
      if (!presupuesto || !presupuesto.desgloce) return 0
      return Number(presupuesto.desgloce.total) || 0
    },
    budgetKey (presupuesto) {
      return `${presupuesto.id}-${presupuesto.orden_id || 'direct'}`
    },
    messageSenderLabel (mensaje) {
      const labels = {
        cliente: 'Cliente',
        admin_propiedad: 'Admin. propiedad',
        profesional: 'Profesional',
        administrador: 'Administrador'
      }
      return labels[mensaje && mensaje.sender_type] || 'Sistema'
    },
    messageBubbleClass (mensaje) {
      const type = mensaje && mensaje.sender_type
      if (type === 'cliente' || type === 'admin_propiedad') return 'from-client'
      if (type === 'profesional') return 'from-professional'
      if (type === 'administrador') return 'from-admin'
      return 'from-system'
    },
    messageText (mensaje) {
      if (!mensaje) return ''
      return mensaje.mensaje || mensaje.mensaje_original || 'Mensaje sin texto'
    },
    messagePhotos (mensaje) {
      return mensaje && Array.isArray(mensaje.fotos) ? mensaje.fotos : []
    }
  }
}
</script>

<style scoped>
.column-right{
  background-color:#f2f7fb;
  padding-top:80px;
  padding-left:260px;
  width:100%;
  min-height:100vh;
}

.main-container{
  padding:22px;
}

.request-admin-page{
  color:#0f172a;
}

.request-admin-loader{
  min-height:360px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:12px;
  color:#64748b;
  font-weight:700;
}

.request-admin-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
  margin-bottom:18px;
}

.header-main{
  display:flex;
  align-items:flex-start;
  gap:14px;
  min-width:0;
}

.back-button{
  width:42px;
  height:42px;
  border-radius:8px;
  border:1px solid #dbe3ef;
  background:#fff;
  color:#334155;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
}

.eyebrow{
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  color:#64748b;
}

h1{
  font-size:28px !important;
  line-height:1.15 !important;
  margin:2px 0 8px !important;
  font-weight:800 !important;
  color:#0f172a;
}

.header-meta{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  color:#64748b;
  font-size:13px;
  font-weight:600;
}

.header-actions{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-end;
}

.action-button{
  min-height:40px;
  border-radius:8px;
  padding:0 13px;
  display:inline-flex;
  align-items:center;
  gap:7px;
  font-weight:700;
  font-size:13px;
  text-decoration:none;
  border:1px solid #dbe3ef;
}

.action-button.primary{
  background:#0f172a;
  color:#fff;
  border-color:#0f172a;
}

.action-button.secondary{
  background:#fff;
  color:#0f172a;
}

.summary-grid{
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  gap:12px;
  margin-bottom:18px;
}

.summary-card,
.panel{
  background:#fff;
  border:1px solid #e2e8f0;
  border-radius:8px;
  box-shadow:0 8px 22px rgba(15,23,42,0.05);
}

.summary-card{
  padding:14px;
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
}

.summary-card:nth-child(1){ border-top:3px solid #38bdf8; }
.summary-card:nth-child(2){ border-top:3px solid #22c55e; }
.summary-card:nth-child(3){ border-top:3px solid #818cf8; }
.summary-card:nth-child(4){ border-top:3px solid #f59e0b; }

.summary-icon{
  width:38px;
  height:38px;
  border-radius:8px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 38px;
  font-size:17px;
}

.summary-icon.customer{ background:#e0f2fe; color:#0369a1; }
.summary-icon.orders{ background:#dcfce7; color:#15803d; }
.summary-icon.conversation{ background:#e0e7ff; color:#4338ca; }
.summary-icon.money{ background:#fef3c7; color:#b45309; }

.summary-copy{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:4px;
}

.summary-label,
.detail-line span{
  font-size:11px;
  color:#64748b;
  font-weight:800;
  text-transform:uppercase;
}

.summary-copy strong{
  font-size:16px;
  color:#0f172a;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.summary-copy span:last-child{
  color:#64748b;
  font-size:13px;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.content-grid{
  display:grid;
  grid-template-columns:minmax(0, 1fr) 340px;
  gap:18px;
  align-items:start;
}

.content-main,
.content-side{
  display:flex;
  flex-direction:column;
  gap:18px;
  min-width:0;
}

.panel{
  padding:16px;
  min-width:0;
}

.panel-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:14px;
}

.panel-header h2{
  display:flex;
  align-items:center;
  gap:9px;
  margin:0;
  font-size:16px !important;
  font-weight:800 !important;
  color:#0f172a;
}

.panel-header > span{
  color:#64748b;
  font-size:12px;
  font-weight:800;
}

.panel-title-icon{
  width:28px;
  height:28px;
  border-radius:8px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:14px;
}

.timeline-icon{ background:#e0f2fe; color:#0369a1; }
.conversation-icon{ background:#e0e7ff; color:#4338ca; }
.orders-icon{ background:#dcfce7; color:#15803d; }
.budget-icon{ background:#fef3c7; color:#b45309; }
.customer-icon{ background:#cffafe; color:#0e7490; }
.request-icon{ background:#f1f5f9; color:#334155; }
.professional-icon{ background:#ede9fe; color:#6d28d9; }
.files-icon{ background:#fce7f3; color:#be185d; }
.bot-icon{ background:#fee2e2; color:#b91c1c; }

.status-badge,
.mini-badge{
  display:inline-flex;
  align-items:center;
  min-height:24px;
  border-radius:8px;
  padding:0 9px;
  font-size:12px;
  font-weight:800;
  border:1px solid transparent;
  white-space:nowrap;
}

.status-badge.muted,
.mini-badge.muted{ background:#f1f5f9; color:#475569; border-color:#e2e8f0; }
.status-badge.info,
.mini-badge.info{ background:#e0f2fe; color:#0369a1; border-color:#bae6fd; }
.status-badge.primary,
.mini-badge.primary{ background:#e0e7ff; color:#4338ca; border-color:#c7d2fe; }
.status-badge.warning,
.mini-badge.warning{ background:#fef3c7; color:#b45309; border-color:#fde68a; }
.status-badge.success,
.mini-badge.success{ background:#dcfce7; color:#15803d; border-color:#bbf7d0; }
.status-badge.danger,
.mini-badge.danger{ background:#fee2e2; color:#b91c1c; border-color:#fecaca; }

.timeline{
  position:relative;
  display:flex;
  flex-direction:column;
  gap:14px;
}

.timeline-item{
  display:grid;
  grid-template-columns:18px minmax(0, 1fr);
  gap:10px;
}

.timeline-dot{
  width:10px;
  height:10px;
  margin-top:6px;
  border-radius:50%;
  background:#94a3b8;
}

.timeline-dot.info{ background:#38bdf8; }
.timeline-dot.primary{ background:#6366f1; }
.timeline-dot.warning{ background:#f59e0b; }
.timeline-dot.success{ background:#22c55e; }

.timeline-title{
  font-weight:800;
  color:#0f172a;
}

.timeline-date{
  font-size:12px;
  color:#64748b;
  margin-top:2px;
}

.timeline-item p{
  margin:6px 0 0;
  color:#475569;
  font-size:13px;
  line-height:1.4;
}

.conversation-tabs{
  display:flex;
  gap:8px;
  overflow:auto;
  padding-bottom:8px;
  margin-bottom:12px;
}

.conversation-tab{
  border:1px solid #dbe3ef;
  background:#fff;
  color:#334155;
  border-radius:8px;
  padding:8px 10px;
  display:flex;
  align-items:center;
  gap:8px;
  font-size:13px;
  font-weight:800;
  white-space:nowrap;
}

.conversation-tab small{
  min-width:22px;
  height:22px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background:#f1f5f9;
  border-radius:999px;
}

.conversation-tab.active{
  background:#0f172a;
  border-color:#0f172a;
  color:#fff;
}

.conversation-tab.active small{
  background:rgba(255,255,255,0.18);
}

.request-conversation{
  display:flex;
  flex-direction:column;
  gap:10px;
  max-height:560px;
  overflow:auto;
  padding-right:4px;
}

.conversation-message{
  width:min(82%, 680px);
  border:1px solid #e2e8f0;
  border-radius:8px;
  padding:10px 12px;
}

.conversation-message.from-client{
  align-self:flex-start;
  background:#f8fafc;
}

.conversation-message.from-professional{
  align-self:flex-end;
  background:#eef2ff;
  border-color:#c7d2fe;
}

.conversation-message.from-admin{
  align-self:flex-end;
  background:#ecfeff;
  border-color:#a5f3fc;
}

.conversation-message.from-system{
  align-self:center;
  background:#f1f5f9;
}

.message-heading{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  color:#64748b;
  font-size:12px;
  font-weight:800;
}

.conversation-message p{
  margin:8px 0 0;
  color:#0f172a;
  white-space:pre-wrap;
  line-height:1.45;
}

.message-photos{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
}

.message-photos a,
.asset-list a,
.inline-link{
  color:#2563eb;
  font-weight:800;
  text-decoration:none;
}

.message-original{
  margin-top:8px;
  padding-top:8px;
  border-top:1px solid rgba(100,116,139,0.2);
  color:#64748b;
  font-size:12px;
}

.order-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.order-row{
  border:1px solid #e2e8f0;
  border-radius:8px;
  padding:12px;
  display:grid;
  grid-template-columns:minmax(0, 1fr) 170px;
  gap:14px;
}

.order-title{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  font-weight:800;
}

.order-title a{
  color:#0f172a;
  text-decoration:none;
}

.order-meta{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
  color:#64748b;
  font-size:13px;
  font-weight:700;
  margin-top:6px;
}

.chips-list{
  display:flex;
  flex-wrap:wrap;
  gap:6px;
  margin-top:10px;
}

.service-chip{
  display:inline-flex;
  align-items:center;
  min-height:24px;
  padding:0 8px;
  border-radius:8px;
  background:#f1f5f9;
  color:#334155;
  font-size:12px;
  font-weight:700;
}

.service-chip.muted{
  color:#64748b;
}

.order-side{
  display:flex;
  flex-direction:column;
  align-items:flex-end;
  gap:4px;
  color:#64748b;
  font-size:13px;
  font-weight:700;
}

.order-side strong{
  color:#0f172a;
  font-size:16px;
}

.table-wrap{
  overflow:auto;
}

.admin-table{
  width:100%;
  border-collapse:collapse;
  min-width:720px;
}

.admin-table th,
.admin-table td{
  border-bottom:1px solid #e2e8f0;
  padding:10px 8px;
  vertical-align:top;
  text-align:left;
}

.admin-table th{
  color:#64748b;
  font-size:11px;
  text-transform:uppercase;
  font-weight:900;
}

.admin-table td strong,
.admin-table td small{
  display:block;
}

.admin-table td small{
  color:#64748b;
  margin-top:3px;
}

.text-right{
  text-align:right !important;
}

.contact-panel{
  display:flex;
  flex-direction:column;
  gap:8px;
}

.contact-panel a{
  color:#2563eb;
  font-weight:800;
  text-decoration:none;
  word-break:break-word;
}

.contact-name{
  font-size:18px;
  font-weight:900;
  color:#0f172a;
}

.detail-line{
  display:flex;
  flex-direction:column;
  gap:4px;
  margin-top:8px;
}

.detail-line strong{
  color:#0f172a;
  line-height:1.35;
}

.description-text{
  margin:12px 0 0;
  color:#334155;
  white-space:pre-wrap;
  line-height:1.5;
}

.stack-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.stack-list.compact{
  gap:8px;
}

.list-row{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  padding:10px 0;
  border-bottom:1px solid #e2e8f0;
}

.list-row:last-child{
  border-bottom:0;
}

.list-row div{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:3px;
}

.list-row strong{
  color:#0f172a;
}

.list-row span{
  color:#64748b;
  font-size:13px;
}

.photo-grid{
  display:grid;
  grid-template-columns:repeat(3, 1fr);
  gap:8px;
}

.photo-thumb{
  display:block;
  aspect-ratio:1;
  border-radius:8px;
  border:1px solid #e2e8f0;
  background-size:cover;
  background-position:center;
  background-color:#f1f5f9;
}

.empty-state{
  padding:18px;
  border:1px dashed #cbd5e1;
  border-radius:8px;
  color:#64748b;
  background:#f8fafc;
  font-weight:700;
  text-align:center;
}

@media (max-width: 1100px){
  .summary-grid{
    grid-template-columns:repeat(2, minmax(0, 1fr));
  }

  .content-grid{
    grid-template-columns:1fr;
  }
}

@media (max-width: 780px){
  .column-right{
    padding-left:0;
  }

  .request-admin-header{
    flex-direction:column;
  }

  .header-actions{
    justify-content:flex-start;
  }

  .summary-grid{
    grid-template-columns:1fr;
  }

  .order-row{
    grid-template-columns:1fr;
  }

  .order-side{
    align-items:flex-start;
  }

  .conversation-message{
    width:100%;
  }
}
</style>
